import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import type { BannerEngineConfig } from '@magnetar/admin-api-interfaces'

export interface ConfigDraft {
  cycle_count: string
  per_cycle_capacity: string
  trigger_interval_seconds: string
}

export function draftFrom(config: BannerEngineConfig): ConfigDraft {
  return {
    cycle_count: String(config.cycle_count),
    per_cycle_capacity: String(config.per_cycle_capacity),
    trigger_interval_seconds: String(Math.round(config.trigger_interval_ms / 1000)),
  }
}

export function draftToBody(draft: ConfigDraft) {
  return {
    cycle_count: Number(draft.cycle_count),
    per_cycle_capacity: Number(draft.per_cycle_capacity),
    trigger_interval_ms: Number(draft.trigger_interval_seconds) * 1000,
  }
}

export function draftValid(draft: ConfigDraft): boolean {
  const b = draftToBody(draft)
  return (
    Number.isInteger(b.cycle_count) && b.cycle_count >= 1 && b.cycle_count <= 60 &&
    Number.isInteger(b.per_cycle_capacity) && b.per_cycle_capacity >= 1 && b.per_cycle_capacity <= 50 &&
    Number.isInteger(b.trigger_interval_ms) && b.trigger_interval_ms >= 1000 && b.trigger_interval_ms <= 86400000
  )
}

/** D8 式提示（與 server 同口徑）：一批要播的秒數超過觸發間隔就提醒，不擋存檔 */
export function cycleWarning(draft: ConfigDraft, avgImageDurationMs: number): string | null {
  const b = draftToBody(draft)
  if (!draftValid(draft)) return null
  const cycleMs = b.per_cycle_capacity * avgImageDurationMs
  if (cycleMs <= b.trigger_interval_ms) return null
  return `一批約 ${Math.round(cycleMs / 1000)} 秒（${b.per_cycle_capacity} 張 × 平均 ${Math.round(
    avgImageDurationMs / 1000,
  )} 秒），超過觸發間隔 ${Math.round(b.trigger_interval_ms / 1000)} 秒；播畢才會再觸發，實際間隔會被拉長。`
}

interface ConfigFieldsProps {
  idPrefix: string
  draft: ConfigDraft
  onChange: (draft: ConfigDraft) => void
  avgImageDurationMs: number
  disabled?: boolean
}

export function ConfigFields({ idPrefix, draft, onChange, avgImageDurationMs, disabled }: ConfigFieldsProps) {
  const warning = cycleWarning(draft, avgImageDurationMs)
  const set = (key: keyof ConfigDraft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...draft, [key]: e.target.value })
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cycle`}>循環批數 N</Label>
          <Input id={`${idPrefix}-cycle`} type="number" min={1} max={60} value={draft.cycle_count} onChange={set('cycle_count')} disabled={disabled} className="tabular-nums" />
          <p className="text-xs text-muted-foreground">圖的「N 批內次數」以此為分母</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-capacity`}>每批槽位</Label>
          <Input id={`${idPrefix}-capacity`} type="number" min={1} max={50} value={draft.per_cycle_capacity} onChange={set('per_cycle_capacity')} disabled={disabled} className="tabular-nums" />
          <p className="text-xs text-muted-foreground">一批最多幾張圖（庫存定義）</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-interval`}>觸發間隔（秒）</Label>
          <Input id={`${idPrefix}-interval`} type="number" min={1} max={86400} value={draft.trigger_interval_seconds} onChange={set('trigger_interval_seconds')} disabled={disabled} className="tabular-nums" />
          <p className="text-xs text-muted-foreground">播畢即退，自該輪結束起算</p>
        </div>
      </div>
      {warning && (
        <Alert>
          <AlertTitle>批長可能超過間隔</AlertTitle>
          <AlertDescription>{warning}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}
